import { StrictMode, Suspense, lazy } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MotionConfig } from 'framer-motion'
import App from './App'
import { AppUpdater } from './components/AppUpdater'
import { EnvBadge } from './components/EnvBadge'
import { isInsightsPath } from './lib/insightsPath'
import './styles/index.css'

// The landing page as static HTML (scripts/prerender.mjs) is for crawlers and
// the first paint; the app renders its own from here on.
document.getElementById('prerender')?.remove()

// The website dashboard is its own page (/insights), loaded only there.
// eslint-disable-next-line react-refresh/only-export-components -- the entry point, never hot-swapped
const InsightsPage = lazy(() => import('./components/InsightsPage'))
const insights = isInsightsPath(window.location.pathname)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 1000 * 30, retry: 2 },
    mutations: { retry: 1 },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Honours the OS "reduce motion" setting for every transform/layout
          animation in the app; fades still play, as framer documents. */}
      <MotionConfig reducedMotion="user">
        {insights
          ? <Suspense fallback={null}><InsightsPage /></Suspense>
          : <><App /><AppUpdater /></>}
        {/* Mounted here rather than inside App so it survives every early return
            — the login and onboarding screens are exactly the ones where
            "which environment is this?" is easiest to get wrong. Renders nothing
            in production unless ?debug is present. */}
        <EnvBadge />
      </MotionConfig>
    </QueryClientProvider>
  </StrictMode>,
)
